import type { ListProjectProductsParams, ListProjectsParams } from '@amarantos/api-client'
import { z } from 'zod/v3'
import { categoryFilterValues } from '@/features/products'

import { definitionFlowEntrySteps, definitionFlowModes, definitionFlowSteps } from './definition-flow'
import { listAllProjectAssetsParams } from './project-asset-list'

export const projectsSearchSchema = z.object({
	page: z.coerce.number().int().positive().default(1),
	perPage: z.coerce.number().int().positive().max(100).default(10),
	search: z.string().default(''),
})

export type ProjectsSearch = z.infer<typeof projectsSearchSchema>

export function toListProjectsParams(search: ProjectsSearch): ListProjectsParams {
	return {
		page: search.page,
		per_page: search.perPage,
		search: search.search.length > 0 ? search.search : undefined,
	}
}

const baseAssetDialogSearchSchema = z.object({
	/** The project asset (product / document / person) the edit dialog targets. */
	assetId: z.coerce.number().int().positive().optional(),
})

// Create/edit dialog state, shared by the Materiały / Dokumenty / Osoby tabs — the edit dialog
// targets the selected `assetId`, so the `edit` branch needs no id of its own.
const assetDialogStateSchema = z.union([
	z.object({ dialog: z.literal('create') }),
	z.object({ dialog: z.literal('edit') }),
	z.object({ dialog: z.undefined().optional() }),
])

export const projectAssetDialogSearchSchema = baseAssetDialogSearchSchema.and(assetDialogStateSchema)

export type ProjectAssetDialogSearch = z.infer<typeof projectAssetDialogSearchSchema>

export const projectProductsSearchSchema = projectAssetDialogSearchSchema.and(
	z.object({
		search: z.string().default(''),
		productCategory: z.enum(categoryFilterValues).default('all'),
	}),
)

export type ProjectProductsSearch = z.infer<typeof projectProductsSearchSchema>

export type ProjectProductsListFilters = Pick<ProjectProductsSearch, 'search' | 'productCategory'>

export const defaultProjectProductsListFilters: ProjectProductsListFilters = { search: '', productCategory: 'all' }

export function toListProjectProductsParams(filters: ProjectProductsListFilters): ListProjectProductsParams {
	return {
		...listAllProjectAssetsParams,
		search: filters.search.length > 0 ? filters.search : undefined,
		product_category: filters.productCategory === 'all' ? undefined : filters.productCategory,
	}
}

// The definition flow's entry route (`/projects/definition`) — before the project exists only the
// choice and basic-data steps are reachable. Mode has no default: nothing is chosen until a card is.
export const projectDefinitionEntrySearchSchema = z.object({
	mode: z.enum(definitionFlowModes).optional(),
	step: z.enum(definitionFlowEntrySteps).default('choice'),
})

export type ProjectDefinitionEntrySearch = z.infer<typeof projectDefinitionEntrySearchSchema>

// The project-scoped definition route (`/projects/:id/definition`). Mode has no default here
// either — a bare URL restarts the flow at the choice step with nothing preselected, and a step
// outside the mode's sequence is clamped by `resolveDefinitionFlowStep`.
export const projectDefinitionSearchSchema = z.object({
	mode: z.enum(definitionFlowModes).optional(),
	step: z.enum(definitionFlowSteps).default('choice'),
	/** The network the networks step is entering connections into; unset before one is created/picked. */
	networkId: z.coerce.number().int().positive().optional(),
})

export type ProjectDefinitionSearch = z.infer<typeof projectDefinitionSearchSchema>
